import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import dayjs from 'dayjs';
import { FilterFieldName, FilterValues, isFilterFieldName } from '@/components/filterbar/FilterBarContext';
import { CacheKey } from '@/providers/cache-provider/cache-key.enum';
import { useCache } from '@/providers/cache-provider/CacheProvider';
import { ApplicationLogCategory, ApplicationLogSeverity } from '@/modules/application-log/api/application-log.contracts';
import { EventType } from '@/modules/events/api/events/event.contracts';
import { PageSortOption, PageSortOrder } from '@/lib/api/pagination.page.dto';
import {
  BatteryStatusSearchParam,
  LinkedTrackerSearchParam,
  TrackerTypeSearchParam,
} from '@/modules/trackers/api/trackers/tracker.contracts';
import { MovementType } from '@/modules/movements/api/movements.contracts';
import { TransactionType, TransactionStatus, TransactionSource } from '@/modules/transactions/api/transactions/transaction.contracts';

/**
 * Parses a numeric query parameter, returning undefined instead of NaN for malformed values.
 * A NaN here would otherwise be written back to the URL as the literal string 'NaN' and then
 * be sent to the API as e.g. /asset/NaN.
 */
const parseNumericParam = (value: string): number | undefined => {
  const parsed = Number.parseInt(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * A NaN would be written to the URL as the literal string 'NaN', read back as NaN on the next
 * load and then sent to the API as e.g. /asset/NaN, so it is dropped from the query string.
 */
const isWritableFilterValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'number' && Number.isNaN(value)) return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

const useQueryParamsFilters = (initialFilterValues: FilterValues, cacheKey: CacheKey) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { getItem, setItem, hasItem } = useCache();

  const getFilterValuesFromParams = (params: URLSearchParams) => {
    const filterValues: FilterValues = { ...initialFilterValues };

    params.forEach((value, key) => {
      if (isFilterFieldName(key)) {
        switch (key) {
          case FilterFieldName.dateFrom:
          case FilterFieldName.dateTo:
          case FilterFieldName.arrivedAtDateFrom:
          case FilterFieldName.arrivedAtDateTo:
            filterValues[key] = dayjs(value);
            break;
          case FilterFieldName.labelValueIds:
          case FilterFieldName.assetTypeIds:
            filterValues[key] = value
              .split(',')
              .map((id) => Number.parseInt(id))
              .filter((id) => !Number.isNaN(id));
            break;
          case FilterFieldName.assetId:
          case FilterFieldName.locationId:
          case FilterFieldName.assetTypeId:
          case FilterFieldName.toLocationId:
          case FilterFieldName.fromLocationId:
            filterValues[key] = parseNumericParam(value);
            break;
          case FilterFieldName.eventType:
            filterValues[key] = value as EventType;
            break;
          case FilterFieldName.logCategory:
            filterValues[key] = value as ApplicationLogCategory;
            break;
          case FilterFieldName.logSeverity:
            filterValues[key] = value as ApplicationLogSeverity;
            break;
          case FilterFieldName.messageId:
          case FilterFieldName.searchText:
            filterValues[key] = value;
            break;
          case FilterFieldName.pageNumber:
            filterValues[key] = parseNumericParam(value);
            break;
          case FilterFieldName.sortOption:
            filterValues[key] = value as PageSortOption;
            break;
          case FilterFieldName.sortDirection:
            filterValues[key] = value as PageSortOrder;
            break;
          case FilterFieldName.stayTimeInDays:
            filterValues[key] = parseNumericParam(value);
            break;
          case FilterFieldName.trackerType:
            filterValues[key] = value as TrackerTypeSearchParam;
            break;
          case FilterFieldName.linkedTracker:
            filterValues[key] = value as LinkedTrackerSearchParam;
            break;
          case FilterFieldName.batteryStatus:
            filterValues[key] = value as BatteryStatusSearchParam;
            break;
          case FilterFieldName.movementType:
            filterValues[key] = value as MovementType;
            break;
          case FilterFieldName.transactionType:
            filterValues[key] = value as TransactionType;
            break;
          case FilterFieldName.transactionStatus:
            filterValues[key] = value as TransactionStatus;
            break;
          case FilterFieldName.transactionSource:
            filterValues[key] = value as TransactionSource;
            break;
        }
      }
    });
    return filterValues;
  };

  const getInitialFilterValues = (initialFilterValues: FilterValues): FilterValues => {
    if (hasItem(cacheKey)) {
      return {
        ...initialFilterValues,
        ...(getItem<FilterValues>(cacheKey) as FilterValues),
      };
    } else {
      const filterValuesFromURL = getFilterValuesFromParams(searchParams);

      return {
        ...initialFilterValues,
        ...filterValuesFromURL,
      };
    }
  };

  const filters = useRef<FilterValues>(getInitialFilterValues(initialFilterValues));

  const updateUrlWithFilters = (filterValues: FilterValues, replace = false) => {
    const newSearchParams = new URLSearchParams();
    Object.entries(filterValues).forEach(([key, value]) => {
      if (isWritableFilterValue(value)) {
        newSearchParams.set(key, value.toString());
      } else {
        newSearchParams.delete(key);
      }
    });
    setSearchParams(newSearchParams, { replace });
  };

  useEffect(() => {
    if (hasItem(cacheKey)) {
      const cachedFilterValues = getItem<FilterValues>(cacheKey) as FilterValues;
      // Mirroring the cached filters into the URL must not add a history entry: this effect
      // re-runs whenever the cache changes, and the extra entries made the browser back
      // button land on this same page over and over instead of on the previous view.
      updateUrlWithFilters(cachedFilterValues, true);
    }
  }, [cacheKey, getItem, hasItem, setSearchParams]);

  useEffect(() => {
    setItem(cacheKey, filters.current);
  }, [filters.current]);

  const setFiltersToUrl = (filterValues: FilterValues) => {
    updateUrlWithFilters(filterValues);
    setItem(cacheKey, filterValues);

    filters.current = filterValues;
  };

  return { setFiltersToUrl, filters };
};

export default useQueryParamsFilters;
